import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SpotlightContext = createContext();

const STORAGE_KEY = 'clara_spotlight_views';
const MAX_VIEWS = 2; // Show spotlight for first 2 page visits

// Read from localStorage
const getLocalViews = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};

export const useSpotlight = () => {
  const context = useContext(SpotlightContext);
  if (!context) {
    throw new Error('useSpotlight must be used within a SpotlightProvider');
  }
  return context;
};

export const SpotlightProvider = ({ children }) => {
  const [views, setViews] = useState({});
  const [dismissed, setDismissed] = useState(new Set());
  const [activeSpotlight, setActiveSpotlight] = useState(null);
  const fetchedRef = useRef(false);

  // Fetch spotlight views from backend on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchViews = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setViews(getLocalViews());
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/spotlight-views`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setViews(data.spotlightViews || {});
        } else {
          setViews(getLocalViews());
        }
      } catch {
        setViews(getLocalViews());
      }
    };
    fetchViews();
  }, []);

  // Check if a spotlight should be visible
  const shouldShow = useCallback((spotlightId) => {
    if (dismissed.has(spotlightId)) return false;
    const count = views[spotlightId] || 0;
    return count < MAX_VIEWS;
  }, [views, dismissed]);

  // Record a view for a spotlight
  const recordView = useCallback((spotlightId) => {
    setViews(prev => {
      const current = prev[spotlightId] || 0;
      if (current >= MAX_VIEWS) return prev;
      const updated = { ...prev, [spotlightId]: current + 1 };

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      // Persist to backend (non-blocking)
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_API_URL}/api/users/spotlight-views/increment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ spotlightId }),
        }).catch(() => {});
      }

      return updated;
    });
  }, []);

  // Dismiss a spotlight (X button)
  const dismiss = useCallback((spotlightId) => {
    setDismissed(prev => new Set([...prev, spotlightId]));
    if (activeSpotlight === spotlightId) setActiveSpotlight(null);

    // Set high count so it never shows again
    setViews(prev => {
      const updated = { ...prev, [spotlightId]: 999 };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });

    // Persist to backend (non-blocking)
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${process.env.REACT_APP_API_URL}/api/users/spotlight-views/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ spotlightId }),
      }).catch(() => {});
    }
  }, [activeSpotlight]);

  return (
    <SpotlightContext.Provider value={{
      views,
      shouldShow,
      recordView,
      dismiss,
      activeSpotlight,
      setActiveSpotlight,
    }}>
      {children}
    </SpotlightContext.Provider>
  );
};
